/**
 * Wrapper operations — wrap, place, swap, resize, change set, and remove.
 *
 * A wrapper is a placed copy of a library asset (`placeCopy`): ordinary scene
 * nodes whose root records `Provenance.placedFrom`, with one `ContentSlot`
 * container inside it. Whatever stands in the slot is the content; layout fits
 * it. These are the six things an editor does to that arrangement, each as one
 * undo step.
 *
 * | operation           | what it does                                                    |
 * |---------------------|-----------------------------------------------------------------|
 * | {@link wrapContent} | the content exists, then a stage is picked: the stage goes on   |
 * |                     | the floor under the content and the content moves into its slot |
 * | {@link placeWrapper}| the stage first, content later                                  |
 * | {@link swapWrapper} | another asset takes the root's place; the content is carried    |
 * | {@link resizeWrapper}, {@link changeWrapperSet} | a swap to a sibling asset of the family |
 * | {@link removeWrapper}| the content goes back to the wrapper's parent; the copy goes  |
 *
 * ## The rules an operation keeps
 *
 * **One undo step.** Each returns the command that reverses it; push it
 * (`getUndoManager().push`) or compose it. It is a {@link CompositeCommand} of
 * typed commands, so a recording of it replays.
 *
 * **Failure rolls back fully.** The steps that can fail come first: fetching
 * the asset, loading its pieces, the slot the new copy must have, carrying
 * behaviors, a host reparent policy. What they change (the new copy) is put
 * back if one of them throws. The last asynchronous authoring step is content
 * sizing: wrap may give content its item after moving; replacement
 * finishes content sizing before retiring the old copy and rechecks liveness
 * and permissions after that await. An operation that throws leaves the scene
 * as it found it, with the same objects, and
 * returns no command to push. Should a step after the checks throw all the
 * same, the steps before it are undone, newest first.
 *
 * **Content is never deleted to roll back.** Undoing a move puts content back
 * where it stood, which no host policy is asked about again, so a rollback
 * normally finishes. Should undoing a step fail all the same (a fault, or a
 * parent that is gone), a copy that still holds content is not deleted, since
 * the delete would take that content with it, and nothing applied before that
 * step is undone either, for it depends on this one. The operation then throws
 * `NOT_RESTORED`: the copy stays with what is in it, and `command` on the error
 * is what is still applied, as one undo entry, for an undo to finish later.
 *
 * **A copy in a layout is sized by what it holds.** A wrapper root has no
 * `Layout`: it holds one. A child of a row or a slot with no `LayoutItem` is
 * sized by its own scale, 1 m however much it holds, so a root that lands under
 * a `Layout` (wrap, place and swap all place one) gets a Hug `LayoutItem`, and
 * layout answers it with the box of what the copy holds (LAYOUT_ENGINE §5.3).
 * The row then makes room for the whole wrapper, and an outer slot fits it as
 * the size it is. A root anywhere else is left as the asset made it. A
 * wrapper moved into a layout by hand keeps whatever `LayoutItem` it has, or
 * none.
 *
 * **Nothing is stored twice.** Which wrapper, size and set a copy is are which
 * asset it was copied from: `Provenance.placedFrom`, and that asset's CMS
 * metadata, which the host's {@link WrapperLibrary} reads. The fit is derived
 * by layout and saved as the content's plain scale. Wrapping/replacing free geometry
 * authors LayoutItem.measureGeometry so the fit uses rendered bounds.
 *
 * ## A swap
 *
 * Explicit replacement fits free content by authoring its geometry opt-in
 * or Hug item while held unfinished. Undo restores the prior sizing choice.
 *
 * The new copy takes the old root's name, uid, place among its siblings and
 * local transform, so anything that targets the wrapper root survives it, and
 * the slot's children move across with their local transforms. It also takes
 * the old root's visibility and every behavior on it except `Provenance` and
 * `Layout`, which are the asset's. A type the new asset's root declares itself
 * stays the asset's and is not overridden. Nothing records which behaviors a
 * user added, so "all but what the asset owns" is the closest that can be said.
 *
 * A `LayoutItem` on the old root keeps its placement: position mode, inset,
 * flex grow and shrink, and cross-axis alignment. Its size does not carry,
 * since it sized the old asset: every axis becomes Hug, and the explicit and
 * min/max sizes are cleared, so the new root takes the box of what it holds.
 *
 * Anything the user put elsewhere inside the old copy is not carried: the copy
 * is a frozen instance of its asset, and a swap replaces the instance.
 * Hosts can call {@link findWrapperExtras} to confirm that loss before a swap.
 * Undo restores the old copy with everything that was in it.
 *
 * The steps are ordered so the old copy is gone before the new one is rebuilt
 * on redo, and the new one is gone before the old is rebuilt on undo. The
 * engine keeps a uid nobody else holds, so a rebuilt node takes the uid it was
 * recorded with. The one moment two nodes hold a uid is while the new copy is
 * placed with the old root's (`PlacedCopyOptions.uid`); the old one is retired
 * before the operation returns.
 */

import { Quaternion } from '@babylonjs/core/Maths/math.vector';

import type { SceneObject } from '../../core/SceneObject';
import type { BehaviorData } from '../../scenedata/behaviordata';
import type { LayoutItemData } from '../../scenedata/behaviors/layoutitemdata';
import type { UndoCommand } from '../undo/UndoCommand';
import { Provenance } from '../../behaviors/provenance';
import { ContentSlot } from '../../behaviors/contentslot';
import { Layout, invalidateNearestLayout, isLayoutSizeProvider, isLayoutSizeReceiver } from '../../behaviors/layout';
import { LayoutItem } from '../../behaviors/layoutitem';
import { GenericBehavior } from '../../behaviors/genericbehavior';
import { getBehaviorConstructor } from '../../behaviors/BehaviorRegistry';
import { AddBehaviorCommand } from '../undo/AddBehaviorCommand';
import { BehaviorCommitCommand } from '../undo/BehaviorCommitCommand';
import { PlacedCopyCommand } from '../undo/PlacedCopyCommand';
import { ReparentCommand } from '../undo/ReparentCommand';
import { ReplacedCopyCommand } from '../undo/ReplacedCopyCommand';
import { CompositeCommand, whileSettling } from '../undo/CompositeCommand';
import { TransformCommand, applySnapshot } from '../undo/TransformCommand';
import type { TransformSnapshot } from '../undo/TransformCommand';
import { Logger } from '../../utils/Logger';

// ── The host's side ──────────────────────────────────────────────────────────

/**
 * Which wrapper an asset is, in the terms the CMS knows it by: the
 * `family` of sizes and sets it belongs to, and its `size` and `set` within
 * it. Values are the CMS metadata's own strings.
 */
export interface WrapperAssetKey {
    family: string;
    size: string;
    set: string;
}

/**
 * How the engine finds a wrapper's siblings. The engine fetches no catalog:
 * the assets and their metadata live in the CMS, and the host reads them.
 * Resize and change set are a swap to a sibling, found through this.
 */
export interface WrapperLibrary {
    /**
     * The family, size and set of the wrapper asset behind `assetUrl` (the
     * `Provenance.placedFrom` form, `<api>/assets/<id>/file`), or `null` when
     * that asset is not a wrapper or has no such metadata.
     */
    describe(assetUrl: string): Promise<WrapperAssetKey | null> | WrapperAssetKey | null;
    /**
     * The URL of the asset with this family, size and set, or `null` when the
     * family has none.
     */
    find(key: WrapperAssetKey): Promise<string | null> | string | null;
}

// ── Errors ───────────────────────────────────────────────────────────────────

/**
 * Why an operation stopped before it changed anything.
 *
 * - `NOT_A_WRAPPER`: the object is not a placed copy (no `placedFrom`)
 * - `NO_SLOT` / `AMBIGUOUS_SLOT`: the copy has no `ContentSlot`, or has several
 * - `NO_PARENT`: the object is the scene root
 * - `REFUSED`: a reparent policy or reference subtree refused a move, or a
 *   behavior on a wrapper root cannot be carried to its replacement
 * - `CHANGED`: the scene changed while the new asset loaded
 * - `SAME_ASSET`: the wrapper already is that asset
 * - `NOT_A_WRAPPER_ASSET`: the library does not know the wrapper's asset
 * - `NO_SIBLING`: the library has no asset for the family in that size or set
 * - `NOT_RESTORED`: the operation failed, and undoing what it had done failed
 *   too. Unlike every other code the scene is not as it was: see the header
 */
export type WrapperErrorCode =
    | 'NOT_A_WRAPPER' | 'NO_SLOT' | 'AMBIGUOUS_SLOT' | 'NO_PARENT' | 'REFUSED' | 'CHANGED'
    | 'SAME_ASSET' | 'NOT_A_WRAPPER_ASSET' | 'NO_SIBLING' | 'NOT_RESTORED';

/**
 * An operation refused to start, or stopped and rolled back. The scene is as
 * it was and nothing was pushed to the undo stack. `NOT_RESTORED` is the one
 * way that is not so.
 */
export class WrapperOperationError extends Error {
    /**
     * `NOT_RESTORED` only: what is still applied, as one undo entry. It is
     * already applied, like the command of a finished operation: push it, and an
     * undo takes back what the rollback could not.
     */
    readonly command?: UndoCommand;

    /** @param details `cause`: the failure that started the rollback; `command`: see {@link WrapperOperationError.command}. */
    constructor(
        readonly code: WrapperErrorCode,
        message: string,
        details: { cause?: unknown; command?: UndoCommand } = {},
    ) {
        super(`[wrappers] ${message}`, 'cause' in details ? { cause: details.cause } : undefined);
        this.name = 'WrapperOperationError';
        if (details.command) this.command = details.command;
    }
}

// ── Finding a wrapper ────────────────────────────────────────────────────────

function placedFromOf(object: SceneObject): string | undefined {
    const from = object.getBehavior(Provenance)?.getData().placedFrom;
    // '' is "no link", the same as absent.
    return typeof from === 'string' && from !== '' ? from : undefined;
}

/**
 * The `ContentSlot` containers among a copy's own nodes. The search enters
 * neither a slot (what stands in one is content, and can hold a wrapper of its
 * own), nor a reference (its subtree is derived), nor another copy (its slot is
 * its own).
 */
function slotsOf(root: SceneObject): SceneObject[] {
    const slots: SceneObject[] = [];
    const visit = (object: SceneObject): void => {
        if (object.getBehavior(ContentSlot)) {
            slots.push(object);
            return;
        }
        if (object.isSceneReference) return;
        for (const child of object.getChildren()) {
            if (placedFromOf(child) === undefined) visit(child);
        }
    };
    visit(root);
    return slots;
}

/**
 * The slot of a wrapper root, or `null` when it has none or has several. The
 * operations refuse those (`NO_SLOT`, `AMBIGUOUS_SLOT`) rather than guess.
 */
export function findWrapperSlot(root: SceneObject): SceneObject | null {
    const slots = slotsOf(root);
    return slots.length === 1 ? slots[0] : null;
}

/**
 * The wrapper root at or above `object`: the nearest placed copy that has a
 * slot. This is how an editor turns "the piece the creator clicked" into "the
 * wrapper it belongs to". Content inside a slot reaches the wrapper it stands
 * in, and content that is itself a wrapper reaches its own first.
 */
export function findWrapper(object: SceneObject): SceneObject | null {
    for (let node: SceneObject | null = object; node; node = node.getParent()) {
        if (placedFromOf(node) !== undefined && slotsOf(node).length > 0) return node;
    }
    return null;
}

/** The content standing in a wrapper: the slot's children, in order. Empty for an unfilled wrapper. */
export function findWrapperContent(root: SceneObject): SceneObject[] {
    return findWrapperSlot(root)?.getChildren() ?? [];
}

/**
 * User-added nodes inside a wrapper that a swap would discard, excluding slot
 * content and the asset's referenced pieces. Only the topmost such nodes are
 * returned, in tree order.
 */
export function findWrapperExtras(root: SceneObject): SceneObject[] {
    if (placedFromOf(root) === undefined) return [];
    const slots = slotsOf(root);
    if (slots.length !== 1) return [];

    const slot = slots[0];
    const slotPath = new Set<SceneObject>();
    for (let node: SceneObject | null = slot; node; node = node.getParent()) {
        slotPath.add(node);
        if (node === root) break;
    }

    const extras: SceneObject[] = [];
    const visit = (object: SceneObject): void => {
        if (object === slot) return;
        if (object !== root && !slotPath.has(object)) {
            if (!object.isSceneReference) extras.push(object);
            return;
        }
        if (object.isSceneReference) return;
        object.getChildren().forEach(visit);
    };
    visit(root);
    return extras;
}

function requireSlot(root: SceneObject): SceneObject {
    if (placedFromOf(root) === undefined) {
        throw new WrapperOperationError('NOT_A_WRAPPER', `"${root.name}" is not a placed copy of a library asset`);
    }
    const slots = slotsOf(root);
    if (slots.length === 0) {
        throw new WrapperOperationError('NO_SLOT', `"${root.name}" has no ContentSlot, so nothing can stand in it`);
    }
    if (slots.length > 1) {
        throw new WrapperOperationError('AMBIGUOUS_SLOT', `"${root.name}" has ${String(slots.length)} ContentSlots; exactly one is needed`);
    }
    return slots[0];
}

// ── Results ──────────────────────────────────────────────────────────────────

/** What an operation did, and the single undo entry that reverses it. */
export interface WrapperOperation {
    /** The wrapper root standing after the operation; `null` after {@link removeWrapper}. */
    wrapper: SceneObject | null;
    /** The content roots the operation moved: the slot's children. Empty for {@link placeWrapper}. */
    content: SceneObject[];
    /** One undo step for the whole operation. Push it or compose it; it is already applied. */
    command: UndoCommand;
}

export interface PlaceWrapperOptions {
    /** A name for the copy's root. Default: the asset's root name, made unique among its siblings. */
    name?: string;
    /**
     * Where the root stands, in its parent's space. Default: the origin. Under a
     * `Layout` parent the flow places it, as it places any child, and this is
     * only where it stands until the flow has run.
     */
    position?: [number, number, number];
    /** The root's rotation `[x, y, z, w]`. Default: none. */
    rotation?: [number, number, number, number];
    /** The root's place among its parent's children. Default: last. */
    index?: number;
}

export interface WrapContentOptions {
    /** A name for the copy's root. Default: the asset's root name, made unique among its siblings. */
    name?: string;
}

// ── The six operations ───────────────────────────────────────────────────────

/**
 * Put a stage under content that already exists.
 *
 * The copy goes under the content's parent, on that parent's floor: the
 * content's local x and z, y = 0, and the content's yaw (an asset's origin is
 * its floor contact, so a copy anchored at the content's own height would stand
 * a slot-height above wherever the content floated). It takes the content's
 * place among its siblings, in the flow or out of it, as the content stood,
 * and the content moves into the slot, where layout fits it. Out-of-flow
 * content hands its place (`Absolute`, its `inset`) to the copy and flows in
 * the slot; {@link removeWrapper} gives it back. Undo puts the content back
 * exactly where it was.
 *
 * A content root that has neither a `Layout` nor a `LayoutItem` gets a Hug
 * `LayoutItem`, because the slot fits only a layout-managed child. It stays
 * after {@link removeWrapper}, including any geometry opt-in: outside a
 * layout Hug imposes no constraint; under a new Layout it measures that
 * geometry. A root with a `Layout` is already managed and carries its item fields.
 *
 * @param assetUrl the wrapper asset, in its `placedFrom` form.
 */
export async function wrapContent(
    content: SceneObject,
    assetUrl: string,
    options: WrapContentOptions = {},
): Promise<WrapperOperation> {
    const scene = content.traceScene;
    const parent = content.getParent();
    if (!parent) throw new WrapperOperationError('NO_PARENT', `"${content.name}" has no parent to stand a wrapper in`);

    const place = outOfFlowPlace(content, parent);
    const root = await scene.placeCopy(assetUrl, parent, placement(parent, options.name, place));
    const commands: UndoCommand[] = [];
    try {
        const slot = requireSlot(root);
        // The asset took time to arrive: the content may not be where it was,
        // nor placed as the copy's item was made from.
        assertStillThere(content, parent);
        if (JSON.stringify(outOfFlowPlace(content, parent)) !== JSON.stringify(place)) {
            throw new WrapperOperationError('CHANGED', `"${content.name}" was re-placed while the wrapper was pending`);
        }
        assertMovable([content], slot);

        // Everything from here on changes the scene, and what could refuse has
        // been asked. The one await left is giving the content its item, and it
        // comes after the move, not before: an item parked outside a layout
        // sizes nothing, and says so.
        const sizing = contentSizingIntent(content);
        const { needsItem, geometryEdit } = sizing;
        const before = content.getPath();
        const index = parent.getChildren().indexOf(content);
        applySnapshot(root, anchorOnFloor(content));
        commands.push(PlacedCopyCommand.placed(root, { index }));
        // The content is in the slot with no box for the length of the await,
        // so it is held as unfinished: the solve that runs in between would
        // find a slot child layout cannot fit, and say so. Undo and redo hold
        // it the same way (`settle`).
        await whileSettling(needsItem || geometryEdit || place.positionMode ? [content] : [], async () => {
            // Content that hands its out-of-flow place to the copy keeps its
            // local transform: the slot's flow places it, and the transform
            // it keeps is the one its Absolute place had, which layout gives
            // back to it when an undo makes it Absolute again.
            commands.push(ReparentCommand.perform(content, slot, { preserveWorld: !place.positionMode }));
            await authorContentSizing(content, sizing, commands);
            // The copy took the content's out-of-flow place: in the slot the
            // content is an ordinary child, which the slot fits.
            if (place.positionMode) await setPlace(content, {}, commands);
        });

        return {
            wrapper: root,
            content: [content],
            command: new CompositeCommand(
                `Wrap ${content.name}`,
                commands,
                needsItem || geometryEdit || place.positionMode ? { settle: [{ object: content, path: before }] } : {},
            ),
        };
    } catch (error) {
        assertRestored(await rollback(commands), `Wrap ${content.name}`, error);
        discard(root);
        throw error;
    }
}

/**
 * Put a stage in the scene with nothing in it: content is added into its slot
 * later, as an ordinary child. Undo removes the copy.
 */
export async function placeWrapper(
    parent: SceneObject,
    assetUrl: string,
    options: PlaceWrapperOptions = {},
): Promise<WrapperOperation> {
    const scene = parent.traceScene;
    const root = await scene.placeCopy(assetUrl, parent, placement(parent, options.name));
    try {
        requireSlot(root);
        if (options.position || options.rotation) {
            const at = TransformCommand.capture(root);
            applySnapshot(root, {
                position: options.position ?? at.position,
                rotation: options.rotation ?? at.rotation,
                scale: at.scale,
            });
            // Layout may have arranged the copy before the transform was
            // written (placing it asked for a solve, and it ran while the
            // asset was still on its way back). Under a Layout parent the flow
            // owns the position: ask again, so what stands now is what a redo
            // rebuilds.
            invalidateNearestLayout(root);
        }
        const command = PlacedCopyCommand.placed(root, options.index !== undefined ? { index: options.index } : {});
        return { wrapper: root, content: [], command: new CompositeCommand(`Place ${root.name}`, [command]) };
    } catch (error) {
        discard(root);
        throw error;
    }
}

/**
 * Replace a wrapper with a copy of another asset, carrying its content across.
 *
 * See the header for what the new root keeps and what it does not. The content
 * keeps its local transform and is refitted to the new slot by layout. Undo
 * gives back the old copy as it was, references and all, and puts the content
 * back into it.
 *
 * @throws `SAME_ASSET` when the wrapper already is that asset. Swapping to it
 *   would only discard what the creator did inside the copy.
 */
export function swapWrapper(wrapper: SceneObject, assetUrl: string): Promise<WrapperOperation> {
    return swap(wrapper, assetUrl, 'Swap');
}

/**
 * Stand the wrapper at another size: a swap to the same wrapper and set in
 * that size, found through the library. Only a few wrappers have more than one
 * size.
 */
export async function resizeWrapper(
    wrapper: SceneObject,
    size: string,
    library: WrapperLibrary,
): Promise<WrapperOperation> {
    return swapToSibling(wrapper, library, key => ({ ...key, size }), `Resize to ${size}`);
}

/**
 * Change the set a wrapper is staged in: a swap to the same wrapper and size in
 * that set, found through the library. A set differs from another in much more
 * than the pieces' `src`, so it is a swap and not a Form swap.
 */
export async function changeWrapperSet(
    wrapper: SceneObject,
    set: string,
    library: WrapperLibrary,
): Promise<WrapperOperation> {
    return swapToSibling(wrapper, library, key => ({ ...key, set }), `Change set to ${set}`);
}

/**
 * Take the copy away and leave its content where the wrapper stood.
 *
 * The content goes to the wrapper's parent, at the wrapper's place, keeping its
 * world position. A scale layout had claimed as the fit is released: it goes
 * back to 1. What the pre-wrap transform was is a past fact the scene never
 * stored, so this keeps where the content is and lets go of the fit; undo,
 * within the session, restores everything exactly.
 *
 * One exception: lone content with a layout item, in a copy that stands out
 * of a layout's flow, takes that place back (`Absolute`, its `inset`) and
 * keeps its local transform, the one it kept when {@link wrapContent} took
 * its place, so it stands where it stood before the wrap.
 */
export async function removeWrapper(wrapper: SceneObject): Promise<WrapperOperation> {
    const parent = wrapper.getParent();
    if (!parent) throw new WrapperOperationError('NO_PARENT', `"${wrapper.name}" has no parent`);
    const slot = requireSlot(wrapper);
    const content = slot.getChildren();
    assertMovable(content, parent);

    const index = parent.getChildren().indexOf(wrapper);
    // See the exception above. Content with no item has nowhere to hold the
    // place, so it is moved like any other: keeping its world position.
    const lone = content.length === 1 ? content[0] : null;
    const place = lone && (lone.getBehavior(Layout) || lone.getBehavior(LayoutItem))
        ? outOfFlowPlace(wrapper, parent) : {};
    const commands: UndoCommand[] = [];
    try {
        content.forEach((child, i) => {
            const fitted = Layout.isFitted(child);
            commands.push(ReparentCommand.perform(child, parent, { index: index + i, preserveWorld: !place.positionMode }));
            if (fitted) commands.push(scaleToOne(child));
        });
        if (place.positionMode) await setPlace(lone!, place, commands);
        commands.push(PlacedCopyCommand.remove(wrapper));
        return {
            wrapper: null,
            content,
            command: new CompositeCommand(`Remove ${wrapper.name}`, commands),
        };
    } catch (error) {
        assertRestored(await rollback(commands), `Remove ${wrapper.name}`, error);
        throw error;
    }
}

// ── swap, and the two operations that are one ────────────────────────────────

async function swapToSibling(
    wrapper: SceneObject,
    library: WrapperLibrary,
    change: (key: WrapperAssetKey) => WrapperAssetKey,
    label: string,
): Promise<WrapperOperation> {
    requireSlot(wrapper);
    // A wrapper has a placedFrom: `requireSlot` refuses anything that has none.
    const from = placedFromOf(wrapper)!;
    const key = await library.describe(from);
    if (!key) {
        throw new WrapperOperationError('NOT_A_WRAPPER_ASSET', `the library does not know ${from} as a wrapper asset`);
    }
    const wanted = change(key);
    if (wanted.size === key.size && wanted.set === key.set) {
        throw new WrapperOperationError('SAME_ASSET', `"${wrapper.name}" already is ${key.family} in ${key.size}, ${key.set}`);
    }
    const url = await library.find(wanted);
    if (!url) {
        throw new WrapperOperationError('NO_SIBLING', `the library has no ${wanted.family} in ${wanted.size}, ${wanted.set}`);
    }
    return swap(wrapper, url, label);
}

async function swap(wrapper: SceneObject, assetUrl: string, label: string): Promise<WrapperOperation> {
    const scene = wrapper.traceScene;
    const parent = wrapper.getParent();
    if (!parent) throw new WrapperOperationError('NO_PARENT', `"${wrapper.name}" has no parent`);
    requireSlot(wrapper);
    if (placedFromOf(wrapper) === assetUrl) {
        throw new WrapperOperationError('SAME_ASSET', `"${wrapper.name}" already is a copy of ${assetUrl}`);
    }
    // The new copy is placed with the old root's uid: it is the wrapper's
    // identity. The two hold it together until the old one is removed below,
    // inside this operation, as `PlacedCopyOptions.uid` describes.
    const root = await scene.placeCopy(assetUrl, parent, {
        ...placement(parent),
        ...(wrapper.uid !== undefined ? { uid: wrapper.uid } : {}),
    });
    const commands: UndoCommand[] = [];
    let newCopyRecorded = false;
    try {
        assertCarriedBehaviorsSupported(wrapper, root);
        const newSlot = requireSlot(root);
        assertStillThere(wrapper, parent);
        for (const data of carriedBehaviors(wrapper)) {
            // Added, not snapshot-restored: the cleared sizes then read their
            // schema defaults, as they do on every rebuild and reopen, so undo,
            // redo and the save all see the same item.
            if (data['@type'] === LayoutItem.behaviorType) {
                root.removeBehavior(LayoutItem);
                await root.addBehavior(LayoutItem, data as LayoutItemData);
                continue;
            }
            // A type the new root declares is the asset's: not overridden.
            if (root.getBehavior(data['@type'])) continue;
            await root.addBehavior(data['@type'], data);
        }
        // A stage the user hid stays hidden.
        if (!wrapper.active) root.setActive(false);

        // The assets are ready. Ask what could be refused before moving;
        // changed content sizing is held unfinished until the swap completes.
        assertStillThere(wrapper, parent);
        const oldSlot = requireSlot(wrapper);
        const content = oldSlot.getChildren();
        assertMovable(content, newSlot);
        assertMovable(content, parent);

        const sizing = content.map(child => ({ child, intent: contentSizingIntent(child), path: child.getPath() }));
        const held = sizing.filter(({ intent }) => intent.needsItem || intent.geometryEdit);
        const playMode = scene.isPlayMode();
        const names = content.map(child => child.name);
        const name = wrapper.name;
        const index = parent.getChildren().indexOf(wrapper);
        applySnapshot(root, TransformCommand.capture(wrapper));

        // The content steps out to the parent for the length of the swap: it
        // must not be inside the old copy when that goes, or in the new one
        // before it exists (on redo, it does not yet).
        await whileSettling(held.map(entry => entry.child), async () => {
            // Finish async sizing before retiring the old copy, then recheck
            // the same liveness and host permissions that guarded the move.
            for (const { child, intent } of held) await authorContentSizing(child, intent, commands);
            assertStillThere(wrapper, parent);
            assertStillThere(root, parent);
            if (requireSlot(root) !== newSlot) throw new WrapperOperationError('CHANGED', 'Wrapper slot changed while content sizing');
            for (const child of content) assertStillThere(child, oldSlot);
            if (scene.isPlayMode() !== playMode) throw new WrapperOperationError('CHANGED', 'Scene play mode changed while content sizing');
            assertMovable(content, newSlot);
            assertMovable(content, parent);
            performAll(commands, content, child =>
                ReparentCommand.perform(child, parent, { preserveWorld: false }));
            const removal = PlacedCopyCommand.remove(wrapper);
            commands.push(removal);
            scene.renameSceneObject(root, name);
            // The new root is the old one to whatever holds it by identity (an
            // interaction target, say): it takes over the old root's ref.
            const arrival = PlacedCopyCommand.replacing(removal, root, { index });
            newCopyRecorded = true;
            // From here the two are one step, undone in the order they were done and
            // recorded as the pair they are, so a recording keeps the identity.
            commands[commands.indexOf(removal)] = new ReplacedCopyCommand(`Replace ${name}`, removal, arrival);
            performAll(commands, content, (child, i) =>
                ReparentCommand.perform(child, newSlot, { preserveWorld: false, name: names[i] }));
        });
        return { wrapper: root, content, command: new CompositeCommand(`${label} ${name}`, [...commands], {
            settle: held.map(({ child, path }) => ({ object: child, path })),
        }) };
    } catch (error) {
        // A new copy that was never recorded is not something the rollback will
        // remove, and it still holds the old root's uid: the rollback would
        // rebuild the old copy under a fresh one. Once it is recorded, undoing
        // its step removes it first, in the right place.
        if (!newCopyRecorded) discard(root);
        assertRestored(await rollback(commands), `${label} ${wrapper.name}`, error);
        discard(root);
        throw error;
    }
}

/**
 * What of the old root goes to the new one: everything but what the asset owns
 * (see the header). Checked after placement because this needs the new root's
 * declared behavior types; if it refuses, the catch discards the new copy.
 */
const NOT_CARRIED = new Set(['Provenance', 'Layout']);

function assertCarriedBehaviorsSupported(wrapper: SceneObject, replacement: SceneObject): void {
    for (const behavior of wrapper.getBehaviors()) {
        const type = behavior.serialize()['@type'];
        if (NOT_CARRIED.has(type)) continue;
        if (type !== LayoutItem.behaviorType && replacement.getBehavior(type)) continue;
        if (behavior instanceof GenericBehavior || !getBehaviorConstructor(type)) {
            throw new WrapperOperationError(
                'REFUSED',
                `behavior "${type}" on wrapper "${wrapper.name}" is generic or unregistered and cannot be carried`,
            );
        }
    }
}

function carriedBehaviors(wrapper: SceneObject): BehaviorData[] {
    return wrapper.getBehaviors()
        .map(behavior => behavior.serialize())
        .filter(data => !NOT_CARRIED.has(data['@type']))
        .map(data => data['@type'] === LayoutItem.behaviorType ? resetLayoutItemSize(data) : data);
}

function resetLayoutItemSize(data: BehaviorData): BehaviorData {
    const carried = structuredClone(data) as BehaviorData & Record<string, unknown>;
    carried.widthMode = 'Hug';
    carried.heightMode = 'Hug';
    carried.depthMode = 'Hug';
    for (const field of [
        'width', 'height', 'depth',
        'minWidth', 'maxWidth', 'minHeight', 'maxHeight', 'minDepth', 'maxDepth',
    ]) {
        delete carried[field];
    }
    return carried;
}

// ── The pieces the operations share ──────────────────────────────────────────

/** An explicit wrap/replacement fits free content; saved scenes do not opt in on load. */
function contentSizingIntent(content: SceneObject) {
    const needsItem = !content.getBehavior(Layout) && !content.getBehavior(LayoutItem);
    const measureGeometry = findWrapper(content) !== content && !content.getBehavior(Layout)
        && !content.getBehaviors().some(behavior => isLayoutSizeProvider(behavior) || isLayoutSizeReceiver(behavior));
    return { needsItem, measureGeometry,
        geometryEdit: measureGeometry && content.getBehavior(LayoutItem)?.getData().measureGeometry !== true };
}

async function authorContentSizing(content: SceneObject, sizing: ReturnType<typeof contentSizingIntent>, commands: UndoCommand[]): Promise<void> {
    if (sizing.needsItem) {
        const item = await content.addBehavior(LayoutItem, { ...HUG_ITEM, ...(sizing.measureGeometry ? { measureGeometry: true } : {}) });
        commands.push(new AddBehaviorCommand(content, LayoutItem.behaviorType, item.getData()));
    } else if (sizing.geometryEdit) {
        const item = content.getBehavior(LayoutItem)!;
        const before = item.getData();
        await item.commit({ measureGeometry: true });
        commands.push(new BehaviorCommitCommand(content, LayoutItem.behaviorType, before, item.getData()));
    }
}

const HUG_ITEM = { widthMode: 'Hug', heightMode: 'Hug', depthMode: 'Hug' } as const;

/**
 * How a copy is placed under `parent`. A root that stands in a layout, in a row
 * or in another wrapper's slot, is a child that layout has to size, and a node
 * with no `LayoutItem` is sized by its own scale: 1 m, however much it holds. So
 * it is placed with a Hug `LayoutItem`, which layout answers with the box of
 * what the copy holds; `place` puts that item out of the flow. A root that
 * stands anywhere else is left as the asset made it: outside a layout a Hug
 * item sizes nothing, and says so.
 */
function placement(
    parent: SceneObject,
    name?: string,
    place: Place = {},
): { name?: string; behaviors?: BehaviorData[] } {
    const item = { '@type': LayoutItem.behaviorType, ...HUG_ITEM, ...place };
    return {
        ...(name !== undefined ? { name } : {}),
        ...(parent.getBehavior(Layout) ? { behaviors: [item] } : {}),
    };
}

/**
 * Where `object` stands out of its layout's flow, a backdrop or an overlay:
 * Absolute, with its inset when it has one. Empty when it stands in the flow.
 * Read as the solver reads a node (`Layout` first, then `LayoutItem`; resolved data).
 */
function outOfFlowPlace(object: SceneObject, parent: SceneObject): Place {
    // Out of a flow there is no place to keep: the solver ignores an item
    // whose parent has no Layout, and `placement` gives the copy none.
    if (!parent.getBehavior(Layout)) return {};
    const data = (object.getBehavior(Layout) ?? object.getBehavior(LayoutItem))?._getResolvedData();
    if (data?.positionMode !== 'Absolute') return {};
    return { positionMode: 'Absolute', ...(data.inset ? { inset: structuredClone(data.inset) } : {}) };
}

/** Write `place` (positionMode, inset; none = in the flow) on `object`'s own item, as one recorded step. */
async function setPlace(object: SceneObject, place: Place, commands: UndoCommand[]): Promise<void> {
    const layout = object.getBehavior(Layout);
    const item = object.getBehavior(LayoutItem);
    if (layout) await writePlace(object, layout, place, commands);
    else if (item) await writePlace(object, item, place, commands);
}

type Place = Pick<LayoutItemData, 'positionMode' | 'inset'>;

async function writePlace<T extends BehaviorData & Place>(
    object: SceneObject,
    behavior: { getData(): T; commitSnapshot(data: T): Promise<unknown> },
    place: Place,
    commands: UndoCommand[],
): Promise<void> {
    const before = behavior.getData();
    const { positionMode: _mode, inset: _inset, ...rest } = before;
    const after = { ...rest, ...structuredClone(place) } as T;
    await behavior.commitSnapshot(after);
    commands.push(new BehaviorCommitCommand(object, before['@type'], before, after));
}

/**
 * The transform a wrapper is anchored with over `content`: its parent's
 * floor under the content, facing the way the content faces. Only the yaw is
 * taken, so the stage stands upright whatever the content's tilt.
 */
function anchorOnFloor(content: SceneObject): TransformSnapshot {
    const t = content.transform;
    const q = t.rotationQuaternion ?? Quaternion.FromEulerVector(t.rotation);
    const yaw = Quaternion.FromEulerAngles(0, q.toEulerAngles().y, 0);
    return {
        position: [t.position.x, 0, t.position.z],
        rotation: [yaw.x, yaw.y, yaw.z, yaw.w],
        scale: [1, 1, 1],
    };
}

/** Let go of a fit scale: the content's scale becomes 1, as one undoable step. */
function scaleToOne(object: SceneObject): UndoCommand {
    const before = TransformCommand.capture(object);
    const after: TransformSnapshot = { ...before, scale: [1, 1, 1] };
    applySnapshot(object, after);
    return new TransformCommand(object, before, after);
}

/**
 * Ask, before anything changes, whether a move would be refused: by a host
 * reparent policy, or because the destination is inside a reference. The move
 * itself would throw, but only once earlier steps had already happened.
 */
function assertMovable(objects: SceneObject[], to: SceneObject): void {
    const scene = to.traceScene;
    for (const object of objects) {
        if (!scene._isReparentAllowed(object, to)) {
            throw new WrapperOperationError('REFUSED', `"${object.name}" cannot be moved into "${to.name}"`);
        }
    }
}

/** The awaits of an operation are the window in which the scene can change under it. */
function assertStillThere(object: SceneObject, parent: SceneObject): void {
    if (object.transform.isDisposed() || object.getParent() !== parent) {
        throw new WrapperOperationError('CHANGED', `"${object.name}" moved or went while the wrapper operation was pending`);
    }
}

/**
 * Perform a step for each of `items` and record it the moment it is done. A step
 * that is refused finds every one before it in `commands`, which is what the
 * rollback puts back: recorded only once all had run, the ones that succeeded
 * would stay where they were put, or go with the copy that holds them.
 */
function performAll<T>(
    commands: UndoCommand[],
    items: T[],
    perform: (item: T, index: number) => UndoCommand,
): UndoCommand[] {
    const done: UndoCommand[] = [];
    items.forEach((item, index) => {
        const command = perform(item, index);
        commands.push(command);
        done.push(command);
    });
    return done;
}

/** What a rollback left: the steps it did not undo, oldest first, and why the undo of each one that failed did. */
interface Unwound {
    left: UndoCommand[];
    failed: Array<{ command: UndoCommand; error: unknown }>;
}

/**
 * Put back what an operation had applied before it failed, newest first,
 * without moving the selection.
 *
 * A step whose undo fails is logged and unwinding goes on: it must not
 * strand the ones before it. Except at a copy that still holds content. Undoing
 * the placement of a copy deletes everything under its root, so what could not
 * be moved out of it would be deleted with it, and every step before this one (the
 * old copy to rebuild beside it, the moves that came out of that copy) needs
 * this one undone first. Unwinding stops there, and what is left is applied.
 */
async function rollback(commands: UndoCommand[]): Promise<Unwound> {
    const newestFirst = [...commands].reverse();
    const left: UndoCommand[] = [];
    const failed: Unwound['failed'] = [];
    for (let i = 0; i < newestFirst.length; i++) {
        const command = newestFirst[i];
        if (heldBy(command).length > 0) {
            left.push(...newestFirst.slice(i));
            break;
        }
        const failure = await undoQuietly(command);
        if (failure) {
            failed.push(failure);
            left.push(command);
        }
    }
    commands.length = 0;
    return { left: left.reverse(), failed };
}

/** What a step would delete along with a copy, when it is the placement of one that holds content. */
function heldBy(command: UndoCommand): SceneObject[] {
    return command instanceof PlacedCopyCommand || command instanceof ReplacedCopyCommand ? command.heldContent() : [];
}

async function undoQuietly(command: UndoCommand): Promise<{ command: UndoCommand; error: unknown } | null> {
    try {
        await command.undo({ silent: true });
        return null;
    } catch (error) {
        Logger.error(`[wrappers] could not roll back "${command.label}":`, error);
        return { command, error };
    }
}

/**
 * After a rollback: nothing left means the scene is as it was, and the failure
 * that started it is what the caller gets. Anything left is not swallowed into a
 * log line: the operation says it could not put things back, and what is still
 * applied goes to the caller as one undo entry.
 */
function assertRestored(unwound: Unwound, what: string, cause: unknown): void {
    if (unwound.left.length === 0) return;
    const messageOf = (error: unknown): string => error instanceof Error ? error.message : String(error);
    const why = unwound.failed.map(({ command, error }) => `"${command.label}": ${messageOf(error)}`).join('; ');
    throw new WrapperOperationError(
        'NOT_RESTORED',
        `${what} failed (${messageOf(cause)}) and could not be put back${why ? ` (${why})` : ''}: `
        + 'no content was deleted, and what is still applied is kept',
        { cause, command: new CompositeCommand(`${what} (not undone)`, unwound.left) },
    );
}

/** Delete a copy that was placed and never recorded. */
function discard(root: SceneObject): void {
    if (!root.transform.isDisposed()) root.traceScene.deleteSceneObject(root);
}
